import { Box, Button, Divider, TextField } from '@mui/material';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { Controller, Form, FormProvider } from 'react-hook-form';
import { useContactInformation } from './hooks';
import { paths } from '@common/types';
import { FormGooglePlacesAutocomplete, TextFormField } from '@components';

interface IContactInformationProps {
  profile: paths[`/profile/{user_id}`]['get']['responses']['200']['content']['application/json'] & {
    id: string;
  };
}

export const ContactInformation = ({ profile }: IContactInformationProps) => {
  const { formMethods, onSubmit, isSubmitEnabled, handleSelectLocation } =
    useContactInformation({
      profile,
    });

  const {
    control,
    formState: { isSubmitting },
  } = formMethods;

  return (
    <Box
      sx={{
        boxShadow: 'initial',
        backgroundColor: 'white',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        height: '100%',
        overflow: 'auto',
      }}
    >
      <FormProvider {...formMethods}>
        <Form
          control={control}
          onSubmit={e => onSubmit(e.event)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '2rem',
            width: '100%',
            height: 'fit-content',
            borderRadius: 0,
            position: 'relative',
            gap: '1rem',
          }}
        >
          <Controller
            name='phone_number'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                label={t('profile.contactInformation.phoneNumber')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                fullWidth
              />
            )}
          />

          <Controller
            name='mobile_phone_number'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                label={t('profile.contactInformation.mobilePhoneNumber')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                fullWidth
              />
            )}
          />

          <Divider
            sx={{ my: '0.5rem', width: '100%' }}
            orientation='horizontal'
          />

          <FormGooglePlacesAutocomplete onChange={handleSelectLocation} />

          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              width: '100%',
            }}
          >
            <TextFormField
              name='street'
              optional
              label={t('profile.contactInformation.addressLine1')}
            />

            <TextFormField
              name='suite'
              label={t('profile.contactInformation.addressLine2')}
              optional
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              width: '100%',
            }}
          >
            <TextFormField
              name='state'
              optional
              label={t('profile.contactInformation.state')}
            />

            <TextFormField
              name='city'
              optional
              label={t('profile.contactInformation.city')}
            />
          </Box>

          <TextFormField
            name='zip_code'
            optional
            label={t('profile.contactInformation.zip')}
          />
          <TextFormField
            name='county'
            optional
            label={t('profile.contactInformation.county')}
          />

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              type='submit'
              variant='contained'
              color='secondary'
              sx={{
                fontSize: '0,875rem',
                fontWeight: 'medium',
                lineHeight: '24px',
                color: 'white',
              }}
              disabled={!isSubmitEnabled}
              loading={isSubmitting}
            >
              {t('general.save')}
            </Button>
          </Box>
        </Form>
      </FormProvider>
    </Box>
  );
};
